import React, { useState, useEffect, useRef } from "react";
import ModalBasic from "./ModalBasic.jsx";
import FileUpload from "./FileUpload.jsx";
import { DangerButton, PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ToggleSwitch from "./ToggleSwitch.jsx";
import DropdownMulti from "./DropdownMulti.jsx";
import { addToast } from "../store/app/action.js";
import { useDispatch, useSelector } from "react-redux";
import { uploadProductDocumentToS3 } from "../api/upload.js";
import { 
  getProductDocuments, 
  createProductDocument, 
  updateProductDocument, 
  deleteProductDocument 
} from "../api/productDocuments.js";
import { FaFilePdf, FaFileWord, FaFileLines, FaPen, FaArrowUpRightFromSquare, FaTrash } from "react-icons/fa6";

const DocumentItem = ({ document, onUpdate, onDelete, selectedMerchant }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(document.display_name || document.original_name);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleSave = async () => {
    if (displayName.trim()) {
      try {
        // Update display name for all stores (since they share the same document)
        const updatePromises = document.stores.map((store, index) => 
          onUpdate(store.id, { display_name: displayName.trim() }, index === document.stores.length - 1)
        );
        
        await Promise.all(updatePromises);
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating display name:', error);
      }
    }
  };

  const handleCancel = () => {
    setDisplayName(document.display_name || document.original_name);
    setIsEditing(false);
  };

  const handleStoreToggle = (storeId, showOnWebsite) => {
    onUpdate(storeId, { show_on_website: showOnWebsite });
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    // Delete all store instances of this document
    document.stores.forEach(store => {
      onDelete(store.id);
    });
    setShowDeleteModal(false);
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="w-4 h-4 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="w-4 h-4 text-blue-500" />;
      default:
        return <FaFileLines className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStoreName = (storeId) => {
    const location = selectedMerchant?.locations?.find(loc => loc.store_id === storeId);
    return location ? (location.subname || location.name || storeId) : storeId;
  };

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
          {getFileIcon(document.original_name)}
        </div>
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="form-input w-full"
                autoFocus
              />
              <div className="flex gap-2">
                <PrimaryButton onClick={handleSave} size="sm">
                  Save
                </PrimaryButton>
                <SecondaryButton onClick={handleCancel} size="sm">
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          ) : (
            <div>
              <h5 className="font-medium text-slate-800 dark:text-slate-100 truncate">
                {document.display_name || document.original_name}
              </h5>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {document.original_name} • {new Date(document.created).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <SecondaryButton
            naked
            onClick={() => setIsEditing(true)}
            disabled={isEditing}
            title="Edit Name"
          >
            <FaPen className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          </SecondaryButton>
          <SecondaryButton
            naked
            onClick={() => window.open(document.url, '_blank')}
            title="View Document"
          >
            <FaArrowUpRightFromSquare className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          </SecondaryButton>
          <SecondaryButton
            naked
            onClick={handleDelete}
            title="Delete Document"
          >
            <FaTrash className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          </SecondaryButton>
        </div>
      </div>
      
      {/* Store-specific toggles */}
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
        <h6 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          Store Visibility
        </h6>
        <div className="space-y-2">
          {document.stores.map(store => (
            <div key={store.id} className="flex items-center justify-between">
              <div>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {getStoreName(store.store_id)}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-500 ml-2">
                  ({store.store_id})
                </span>
              </div>
              <ToggleSwitch
                checked={store.show_on_website}
                onChange={(checked) => handleStoreToggle(store.id, checked)}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <ModalBasic
        modalOpen={showDeleteModal}
        setModalOpen={setShowDeleteModal}
        width="sm"
        title="Delete Document"
      >
        <div className="space-y-4">
          <p className="text-slate-600 dark:text-slate-400">
            Are you sure you want to delete this document from all stores? This action cannot be undone.
          </p>
          <div className="flex gap-2 justify-end">
            <SecondaryButton onClick={() => setShowDeleteModal(false)}>
              Cancel
            </SecondaryButton>
            <DangerButton onClick={confirmDelete}>
              Delete Document
            </DangerButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

const DocumentModal = ({ skuData, setDocumentModalOpen, refreshDocumentCounts }) => {
  const dispatch = useDispatch();
  const { selectedMerchant } = useSelector((state) => state.app);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [loading, setLoading] = useState(false);
  const processingRef = useRef(false);
  
  // Upload step state
  const [uploadStep, setUploadStep] = useState(1); // 1: select file, 2: configure, 3: uploading
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [showOnWebsite, setShowOnWebsite] = useState(true);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);

  // File icon helper function
  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="w-8 h-8 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="w-8 h-8 text-blue-500" />;
      default:
        return <FaFileAlt className="w-8 h-8 text-slate-500" />;
    }
  };

  // Load documents when modal opens
  useEffect(() => {
    if (skuData) {
      loadDocuments();
    }
  }, [skuData]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await getProductDocuments(skuData.sku_id);
      
      // Group documents by URL to handle multiple store assignments
      const groupedDocs = {};
      (response.data || []).forEach(doc => {
        if (!groupedDocs[doc.url]) {
          groupedDocs[doc.url] = {
            id: doc.id,
            url: doc.url,
            key: doc.key,
            original_name: doc.original_name,
            display_name: doc.display_name,
            created: doc.created,
            updated: doc.updated,
            stores: []
          };
        }
        groupedDocs[doc.url].stores.push({
          id: doc.id,
          store_id: doc.store_id,
          store: doc.store,
          show_on_website: doc.show_on_website
        });
      });
      
      setDocuments(Object.values(groupedDocs));
    } catch (error) {
      console.error('Error loading documents:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to load documents",
        timer: 5
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Prevent multiple simultaneous processing
    if (processingRef.current) {
      return;
    }
    
    
    // Validate file types
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      dispatch(addToast({
        type: "error",
        message: `Invalid file types. Only PDF, DOC, and DOCX files are allowed.`,
        timer: 5
      }));
      return;
    }
    
    // Take only the first file
    const file = files[0];
    setSelectedFile(file);
    setDisplayName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension
    setShowOnWebsite(false);
    
    // If multiple locations, default to all selected
    if (selectedMerchant?.locations?.length > 1) {
      setSelectedStoreIds(selectedMerchant.locations.map(loc => loc.store_id));
    } else if (selectedMerchant?.locations?.length === 1) {
      // If only one location, select that one store
      setSelectedStoreIds([selectedMerchant.locations[0].store_id]);
    } else {
      setSelectedStoreIds([]);
    }
    
    // Upload file to S3 first
    processingRef.current = true;
    setUploading(true);
    setUploadStep(3);
    
    try {
      const uploadedUrl = await uploadProductDocumentToS3(file);
      setFileUrl(uploadedUrl);
      
      // Move to configuration step
      setUploadStep(2);
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      dispatch(addToast({
        type: "error",
        message: "Error uploading file to S3",
        timer: 5
      }));
      setUploadStep(1);
    } finally {
      setUploading(false);
      processingRef.current = false;
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !fileUrl) return;
    
    setUploading(true);
    try {
      // Extract bucket and key from URL
      const urlParts = fileUrl.replace('https://cdn.saucey.com/', '').split('/');
      const key = urlParts.join('/');
      
      const documentData = {
        display_name: displayName,
        show_on_website: showOnWebsite,
        url: fileUrl,
        key: key,
        filename: selectedFile.name
      };
      
      const response = await createProductDocument(skuData.sku_id, documentData, selectedStoreIds);
      
      // Reload documents to get the updated list with proper grouping
      await loadDocuments();
      
      // Refresh document counts in the table
      if (refreshDocumentCounts) {
        refreshDocumentCounts([skuData.sku_id]);
      }
      
      dispatch(addToast({
        type: "success",
        message: "Document uploaded successfully",
        timer: 4
      }));
      
      // Reset form
      setUploadStep(1);
      setSelectedFile(null);
      setFileUrl(null);
      setDisplayName("");
      setShowOnWebsite(false);
      setSelectedStoreIds([]);
      
    } catch (error) {
      console.error('Error creating document:', error);
      dispatch(addToast({
        type: "error",
        message: "Error creating document",
        timer: 5
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setUploadStep(1);
    setSelectedFile(null);
    setFileUrl(null);
    setDisplayName("");
    setShowOnWebsite(false);
    setSelectedStoreIds([]);
  };

  const handleDocumentUpdate = async (documentId, updates, showToast = true) => {
    setUpdating(documentId);
    try {
      const response = await updateProductDocument(documentId, updates);
      
      // Update the grouped documents structure
      setDocuments(prev => 
        prev.map(doc => {
          // Find the document that contains the store with this documentId
          const updatedStores = doc.stores.map(store => 
            store.id === documentId ? { ...store, ...updates } : store
          );
          
          // If updating display_name, also update it at the document group level
          const updatedDoc = { ...doc, stores: updatedStores };
          if (updates.display_name) {
            updatedDoc.display_name = updates.display_name;
          }
          
          return updatedDoc;
        })
      );
      
      if (showToast) {
        dispatch(addToast({
          type: "success",
          message: "Document updated successfully",
          timer: 3
        }));
      }
    } catch (error) {
      console.error('Error updating document:', error);
      if (showToast) {
        dispatch(addToast({
          type: "error",
          message: "Error updating document",
          timer: 5
        }));
      }
    } finally {
      setUpdating(null);
    }
  };

  const handleDocumentDelete = async (documentId) => {
    setUpdating(documentId);
    try {
      await deleteProductDocument(documentId);
      
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Refresh document counts in the table
      if (refreshDocumentCounts) {
        refreshDocumentCounts([skuData.sku_id]);
      }
      
      dispatch(addToast({
        type: "success",
        message: "Document deleted successfully",
        timer: 3
      }));
    } catch (error) {
      console.error('Error deleting document:', error);
      dispatch(addToast({
        type: "error",
        message: "Error deleting document",
        timer: 5
      }));
    } finally {
      setUpdating(null);
    }
  };

  if (!skuData) {
    return null;
  }

  const { sku_id, product } = skuData;

  return (
    <ModalBasic
      modalOpen={Boolean(skuData)}
      setModalOpen={() => setDocumentModalOpen(null)}
      width="5xl"
      title={`Documents for ${product.title} ${product.subtitle || ''}`}
    >
      <div className="space-y-6">
        {/* Product Info Header */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <img 
            src={product.image} 
            alt={product.title}
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0" 
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">
              {product.title} {product.subtitle || ''}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              SKU: {sku_id}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brand: {product.company}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">
              {documents.length}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-500">
              document{documents.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        
        {/* Upload Section */}
        <div>
          <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3">
            Upload Document
          </h4>
          
          {/* Step 1: File Selection */}
          {uploadStep === 1 && (
            <div className="space-y-4">
              <FileUpload
                accept={[".pdf", ".doc", ".docx"]}
                multiple={false}
                onChange={handleFileUpload}
                inputContent="Drag & drop a document here or click to browse (PDF, DOC, DOCX only)"
              />
            </div>
          )}
          
          {/* Step 2: Configuration */}
          {uploadStep === 2 && selectedFile && (
            <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="flex items-center gap-3">
                {getFileIcon(selectedFile.name)}
                <div className="flex-1">
                  <h5 className="font-medium text-slate-800 dark:text-slate-100">
                    {selectedFile.name}
                  </h5>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="form-input w-full"
                    placeholder="Enter display name for this document"
                  />
                </div>
                
                {/* Show on Website Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Show on Website
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Make this document visible to customers
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={showOnWebsite}
                    onChange={setShowOnWebsite}
                  />
                </div>
                
                {/* Location Selection */}
                {selectedMerchant?.locations?.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Apply to Locations
                    </label>
                    <DropdownMulti
                      options={selectedMerchant.locations.map(loc => ({
                        id: loc.store_id,
                        label: loc.subname || loc.name || loc.store_id
                      }))}
                      selectedValues={selectedStoreIds}
                      setSelectedValues={setSelectedStoreIds}
                      emptyPlaceholder="Select locations..."
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Choose which locations this document should be available at
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <SecondaryButton onClick={handleCancelUpload}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton 
                  onClick={handleConfirmUpload}
                  disabled={!displayName.trim() || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </PrimaryButton>
              </div>
            </div>
          )}
          
          {/* Step 3: Uploading */}
          {uploadStep === 3 && (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <svg className="animate-spin w-8 h-8 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
              </svg>
              <p>Uploading file...</p>
            </div>
          )}
        </div>
        
        {/* Document List */}
        <div>
          <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3">
            Existing Documents ({documents.length})
          </h4>
          {loading ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <svg className="animate-spin w-8 h-8 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
              </svg>
              <p>Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <svg className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
              <p>No documents uploaded yet</p>
              <p className="text-sm">Upload documents using the area above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => (
                <DocumentItem 
                  key={doc?.id}
                  document={doc}
                  onUpdate={handleDocumentUpdate}
                  onDelete={handleDocumentDelete}
                  selectedMerchant={selectedMerchant}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalBasic>
  );
};

export default DocumentModal;
